// src/resources/Letters/MDSignatureUpload.js
import React, { useEffect, useState } from "react";
import { db, storage } from "../../firebase/firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * 🌟 MDSignatureUpload.js (v2.1 – Final Polished)
 * -------------------------------------------------------
 * Allows MD to upload a single combined Signature + Stamp image.
 * Saves to Firestore:  signatureStampUrl
 * Auto-links image to selected company.
 */

export const MDSignatureUpload = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [signatureStampUrl, setSignatureStampUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const mdEmail = localStorage.getItem("email") || "";

  // 🔹 Load all companies
  useEffect(() => {
    const q = query(collection(db, "companies"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  // 🔹 Handle File Upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedCompany)
      return alert("⚠️ Please select a company first.");

    try {
      setUploading(true);

      const safeName = file.name.replace(/\s+/g, "_");
      const path = `companies/${selectedCompany}/signatureStamp_${Date.now()}_${safeName}`;
      const fileRef = ref(storage, path);

      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const companyRef = doc(db, "companies", selectedCompany);
      await updateDoc(companyRef, {
        signatureStampUrl: url,
        updatedBy: mdEmail,
        updatedAt: new Date(),
      });

      setSignatureStampUrl(url);
      alert("✅ Signature / Stamp uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      alert("❌ Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  /* -------------------------------------------------
     🔹 Render
  ---------------------------------------------------*/
  return (
    <div style={{ padding: 30, maxWidth: 700, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 20, color: "#222" }}>
        🖋️ MD Signature / Stamp Upload
      </h2>

      {/* Select Company */}
      <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
        Select Company
      </label>
      <select
        value={selectedCompany}
        onChange={(e) => setSelectedCompany(e.target.value)}
        style={{
          width: "100%",
          padding: "8px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          marginBottom: "20px",
        }}
      >
        <option value="">-- Select Company --</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      {/* Upload Field */}
      <div>
        <label style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
          Upload Combined Signature / Stamp Image
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={uploading}
        />
        {uploading && (
          <p style={{ color: "#1976d2", marginTop: 8 }}>
            ⏳ Uploading, please wait...
          </p>
        )}

        {signatureStampUrl && (
          <div style={{ marginTop: 15 }}>
            <img
              src={signatureStampUrl}
              alt="Signature/Stamp Preview"
              style={{
                width: "220px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "4px",
                background: "#fff",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            />
            <p style={{ fontSize: "0.9em", color: "#666", marginTop: 6 }}>
              ✅ Uploaded successfully for this company.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
